import {
  ConstraintMode,
  ConstraintProfile,
  AppliedProfile,
} from 'types/ConstraintProfileTypes';
import { CONSTRAINTS } from '../constants';

/**
 * The reconciliation describes what `appliedProfile` should become, given a
 * thread's metadata and the current catalog. Pure — no store reads, no
 * effects, no SDK calls. The hook that owns reconciliation translates the
 * action into a single store mutation (or a no-op).
 *
 *   - `restore`                 : metadata's APPLIED_PROFILE_KEY id resolves
 *                                  in the catalog; reflect it locally.
 *   - `clear`                   : metadata has no APPLIED_PROFILE_KEY (or it
 *                                  is `{}`); local should be null.
 *   - `replace_with_predefiniti`: metadata's id does NOT resolve (profile
 *                                  was deleted elsewhere). Local becomes
 *                                  null AND a cleanup silent-sync must be
 *                                  scheduled so the stale reference in
 *                                  thread metadata is overwritten.
 *   - `noop`                    : local already matches the metadata.
 */
export type ReconciliationAction =
  | { kind: 'noop' }
  | { kind: 'clear' }
  | { kind: 'restore'; profile: ConstraintProfile }
  | { kind: 'replace_with_predefiniti' };

export interface ReconciliationInputs {
  metadata: Record<string, any> | null | undefined;
  profilesByMode: {
    builder: ConstraintProfile[];
    rebalancing: ConstraintProfile[];
  };
  mode: ConstraintMode;
  currentAppliedProfile: AppliedProfile | null;
}

/**
 * Decide how `appliedProfile` should be updated based on a thread-metadata
 * snapshot. This is the single decision point that replaces the three-branch
 * effect — every restoration path goes through it.
 *
 * Invariants the planner enforces:
 *
 *  1. Only `builder` and `rebalancing` modes can hold a profile. Other modes
 *     (e.g. `diagnosis`) short-circuit to `noop`.
 *  2. Missing metadata OR no APPLIED_PROFILE_KEY OR an empty `{}` value all
 *     mean "no profile" → `clear` (or `noop` if already null).
 *  3. A resolvable id => `restore` if not already restored.
 *  4. An unresolvable id => `replace_with_predefiniti` if not already null.
 *     The caller is responsible for triggering the cleanup silent-sync.
 */
export function planAppliedProfileReconciliation({
  metadata,
  profilesByMode,
  mode,
  currentAppliedProfile,
}: ReconciliationInputs): ReconciliationAction {
  if (mode !== 'builder' && mode !== 'rebalancing') {
    return { kind: 'noop' };
  }
  if (!metadata) {
    return { kind: 'noop' };
  }

  const fromMeta = metadata[CONSTRAINTS.APPLIED_PROFILE_KEY] as
    | { id?: string; name?: string }
    | null
    | undefined;

  if (!fromMeta?.id) {
    return currentAppliedProfile === null ? { kind: 'noop' } : { kind: 'clear' };
  }

  const profile = profilesByMode[mode]?.find((p) => p.id === fromMeta.id);
  if (profile) {
    return currentAppliedProfile?.id === profile.id
      ? { kind: 'noop' }
      : { kind: 'restore', profile };
  }

  return currentAppliedProfile === null
    ? { kind: 'noop' }
    : { kind: 'replace_with_predefiniti' };
}
